/**
 * SMS Messages Service
 *
 * Data access layer for sms_messages table.
 * Handles SMS message template operations.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { Database } from '../lib/database.types';

export type SmsMessage = Database['public']['Tables']['sms_messages']['Row'];
export type SmsMessageInsert = Database['public']['Tables']['sms_messages']['Insert'];
export type SmsMessageUpdate = Database['public']['Tables']['sms_messages']['Update'];

/**
 * Get all SMS messages for a location
 */
export async function getSmsMessages(locationId: string, language?: string) {
  try {
    let query = supabase
      .from('sms_messages')
      .select('*')
      .eq('location_id', locationId)
      .order('category', { ascending: true })
      .order('reference', { ascending: true })
      .order('offset_minutes', { ascending: true });

    if (language) {
      query = query.eq('language', language);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get a single SMS message by id
 */
export async function getSmsMessageById(id: string) {
  try {
    const { data, error } = await supabase
      .from('sms_messages')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new SMS message
 */
export async function createSmsMessage(data: SmsMessageInsert) {
  try {
    const { data: newMessage, error } = await supabase
      .from('sms_messages')
      .insert(data)
      .select()
      .single();

    if (error) throw error;

    return { data: newMessage, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update an SMS message
 */
export async function updateSmsMessage(id: string, data: SmsMessageUpdate) {
  try {
    const { data: updatedMessage, error } = await supabase
      .from('sms_messages')
      .update({
        ...data,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data: updatedMessage, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete an SMS message
 */
export async function deleteSmsMessage(id: string) {
  try {
    const { error } = await supabase
      .from('sms_messages')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}
